import React from 'react';
import ButtonTabs from '@ui/tabs/ButtonTabs';
import CaptionGroup, { CaptionGroupDivider } from '@ui/captiongroup/CaptionGroup';
import MonthRangePicker, { MonthRange, MonthRangePreset } from '@ui/selector/MonthRangePicker';
import Tag from '@ui/tag/Tag';
import { OptionsBasis, RevenueScope, VatBasis } from '@app/model/bi/Bi';
import { MAX_MONTHS } from '../Bi.utilities';

// D11 (grill of the Channels report, 2026-10-23) and ROO2-283: the switch row of every BI report as one Screen Part. The
// controls of the report come first (children, for example "Group by"), then the revenue switches (ROO2-236) and the Stay
// months picker (up to MAX_MONTHS months), then the controls after them (after), and under them the scope line and the
// source of the reservations. ROO2-288: Daily Performance has its own Nights range (ROO2-298, D47: any first and last night,
// up to 12 months), so it leaves the picker out.
// No note on the scope options: a 400 px list cuts "Room only (without meals)"; each report says what each one holds.
const SCOPE_OPTIONS: { value: RevenueScope, label: string }[] = [{ value: 'ROOM', label: 'Room only' }, { value: 'TOTAL', label: 'Total' }];
const VAT_OPTIONS: { value: VatBasis, label: string, note?: string }[] = [{ value: 'INCL', label: 'Incl. VAT' }, { value: 'NET', label: 'Net', note: 'excl. VAT' }];
// ROO2-310 D8 (direction A): the Options switch is a group of its own after Revenue, because the rooms on option change the
// room nights and the occupancy too, not only the revenue. On a phone ButtonTabs is a select, as the other groups.
const OPTION_OPTIONS: { value: OptionsBasis, label: string }[] = [{ value: 'EXCLUDE', label: 'Exclude' }, { value: 'INCLUDE', label: 'Include' }];

// ROO2-310: the caption sentence of each state, for the captions of the reports.
export const optionsCaption = (options: OptionsBasis): string => options === 'INCLUDE'
	? 'Options included: each figure counts the rooms on option as sold.'
	: 'Options excluded: the rooms on option are not for sale.';

type Props = {
	scope: RevenueScope,
	vat: VatBasis,
	onScope: (scope: RevenueScope) => void,
	onVat: (vat: VatBasis) => void,
	// ROO2-310 B8: the Options switch; a report that passes no onOptions shows no Options group.
	options?: OptionsBasis,
	onOptions?: (options: OptionsBasis) => void,
	period?: MonthRange,
	onPeriod?: (period: MonthRange) => void,
	presets?: MonthRangePreset[],
	min?: string,
	max?: string,
	// The scope line under the switches, for example "53 channels · 34 of 36 properties"; null while the report loads. With
	// no line (Segmentation), the source of the reservations sits at the right of the switches.
	line?: string | null,
	// The sources of the reservations; none (the Property comparison, ROO2-277) shows no "Source".
	sources: string[],
	children?: React.ReactNode,
	// Controls after the Stay months (Daily Performance: the STLY choice, D3).
	after?: React.ReactNode,
};

const BiSwitchRow: React.FunctionComponent<Props> = ({ scope, vat, onScope, onVat, options, onOptions, period, onPeriod, presets, min, max, line, sources, children, after }) => {
	const source = sources.length === 0 ? null : <p className="flex items-center gap-x-2 text-sm text-gray-500">Source {sources.map(s => <Tag key={s} state="success">{s}</Tag>)}</p>;
	const switches = <div className="grid w-full grid-cols-2 gap-x-2 gap-y-4 pt-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center sm:gap-x-6 sm:gap-y-4">
		{children}
		{/* The caption "Revenue" sits in the frame line, so it never reads as one more option. */}
		<CaptionGroup caption="Revenue" className="col-span-2 w-full sm:col-span-1 sm:w-auto">
			<div className="grid w-full grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-x-3">
				<div className="min-w-0"><ButtonTabs label="Revenue of the page: room only or total" options={SCOPE_OPTIONS} value={scope}
					onChange={v => onScope(v as RevenueScope)} /></div>
				{/* Room only | Total and Incl. VAT | Net choose two different things: the line keeps them apart. */}
				<CaptionGroupDivider className="hidden sm:block" />
				<div className="min-w-0"><ButtonTabs label="VAT of the revenue: included or removed" options={VAT_OPTIONS} value={vat}
					onChange={v => onVat(v as VatBasis)} /></div>
			</div>
		</CaptionGroup>
		{options !== undefined && onOptions !== undefined && <CaptionGroup caption="Options" className="col-span-2 w-full sm:col-span-1 sm:w-auto">
			<div className="w-full min-w-0"><ButtonTabs label="Rooms on option: left out or counted as sold" options={OPTION_OPTIONS} value={options}
				onChange={v => onOptions(v as OptionsBasis)} /></div>
		</CaptionGroup>}
		{/* One period for the whole report. Bare: the frame of the group is its edge, so the frame has the height of the
			frames with ButtonTabs. Two years: a period over a year shows while the user picks it. */}
		{period !== undefined && onPeriod !== undefined && <CaptionGroup caption="Stay months" className="col-span-2 w-full sm:col-span-1 sm:w-auto">
			<div className="flex w-full min-w-0 items-center">
				<MonthRangePicker value={period} onChange={onPeriod} presets={presets ?? []} min={min ?? ''} max={max ?? ''} maxMonths={MAX_MONTHS} years={2}
					variant="bare" label="Stay months of the page" />
			</div>
		</CaptionGroup>}
		{after}
	</div>;

	// ROO2-310: the caption of the Options switch says which figures hold the rooms on option, on every report that has it.
	const caption = options === undefined || onOptions === undefined ? null : optionsCaption(options);
	if (line === undefined) return <div className="flex flex-col gap-2">
		<div className="flex flex-wrap items-center justify-between gap-3">{switches}{source}</div>
		{caption !== null && <p className="text-xs text-gray-500">{caption}</p>}
	</div>;
	return <div className="flex flex-col gap-2">
		{switches}
		<div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
			<p className="text-xs text-gray-500">{caption === null ? (line ?? ' ') : `${line === null ? '' : `${line} · `}${caption}`}</p>
			{source}
		</div>
	</div>;
};

export default BiSwitchRow;
